import { selectSelectionsSlice } from "@/store/selections-selectors";
import { useAppSelector } from "@/store/store-hooks";
import { UPDATE_LOD_STRUCTURES_PRIORITY } from "@faro-lotv/spatial-ui";
import { IElementGenericImgSheet } from "@faro-lotv/ielement-types";
import { useFrame } from "@react-three/fiber";
import { EventHandlers } from "@react-three/fiber/dist/declarations/src/core/events";
import { isEqual } from "es-toolkit";
import { useEffect, useMemo, useState, type JSX } from "react";
import { Matrix4, Vector2 } from "three";
import { DynamicMapLayer } from "./dynamic-map-layer";
import {
  computeDynamicMapPlacement,
  computeDynamicMapScans,
} from "./dynamic-map-selectors";
import { DynamicMapSource } from "./dynamic-map-source";

/** Delay to apply scan visibility changes, to avoid requesting tiles for each toggle of a quick sequence */
const SCANS_DEBOUNCE_MS = 50;

type DynamicMapRendererProps = EventHandlers & {
  /** The loaded dynamic map to render */
  source: DynamicMapSource;
  /** The static overview map replaced by the dynamic one, used for its elevation */
  sheet: IElementGenericImgSheet;
};

/** @returns the tiles of a dynamic overview map for the visible scans, as seen by the current camera */
export function DynamicMapRenderer({
  source,
  sheet,
  ...events
}: DynamicMapRendererProps): JSX.Element {
  const placement = useAppSelector(
    (state) => computeDynamicMapPlacement(state, sheet),
    isEqual,
  );
  const scans = useDebouncedValue(
    useAppSelector((state) =>
      computeDynamicMapScans(
        source.metadata,
        selectSelectionsSlice(state).hiddenScans,
      ),
    ),
    SCANS_DEBOUNCE_MS,
  );

  const layer = useMemo(() => new DynamicMapLayer(source), [source]);
  useEffect(() => () => layer.dispose(), [layer]);

  useEffect(() => {
    layer.setPlacement(
      new Matrix4().fromArray(placement.matrix),
      placement.elevation,
    );
  }, [layer, placement]);

  useEffect(() => layer.setScans(scans), [layer, scans]);

  // Run after controls and animations moved the camera, using the state of the current view
  useFrame(
    ({ camera, size, viewport }) =>
      layer.update(
        camera,
        new Vector2(size.width, size.height).multiplyScalar(viewport.dpr),
      ),
    UPDATE_LOD_STRUCTURES_PRIORITY,
  );

  return <primitive object={layer} dispose={null} {...events} />;
}

/**
 * @returns the value, updated only after it did not change for the given delay
 * @param value to debounce
 * @param delayMs to wait before updating the value
 */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timeout);
  }, [value, delayMs]);
  return debounced;
}
